/**
 * Link previews — the Facebook-style card rendered under a post whose body
 * contains a URL.
 *
 * The shape is deliberately provider-agnostic: `type` says which provider
 * produced the row and the optional fields carry whatever that provider could
 * extract. A consumer renders from the common fields and only reaches for
 * `videoId` when `type` is a video provider.
 *
 * Persistence lives in the Blocks `linkpreview` entity, keyed by `url`, so the
 * same address is scraped once and reused by every post that mentions it. See
 * `docs/blocks-schema-link-preview.txt` for the portal schema.
 */

export const LINK_PREVIEW_TYPES = ["webpage", "youtube"] as const;

/** Discriminator for the preview card. New providers extend this union. */
export type LinkPreviewType = (typeof LINK_PREVIEW_TYPES)[number];

export type LinkPreview = {
  /** Normalised address the preview was extracted from. Also the cache key. */
  url: string;
  type: LinkPreviewType;
  title: string | null;
  description: string | null;
  imageUrl: string | null;
  /** og:site_name, or the provider's own name ("YouTube"). */
  siteName: string | null;
  /** Hostname without `www.`, shown as the card's footer line. */
  domain: string;
  /** Provider media id — the YouTube video id for `type: "youtube"`. */
  videoId: string | null;
};

/**
 * A `linkpreview` row as the gateway returns it. The platform owns the
 * `ItemId` / `CreatedDate` / `LastUpdatedDate` columns; the rest mirror
 * `LinkPreview` one-for-one.
 */
export type StoredLinkPreview = LinkPreview & {
  ItemId: string;
  CreatedDate?: string;
  LastUpdatedDate?: string;
};

export const isLinkPreviewType = (value: unknown): value is LinkPreviewType =>
  typeof value === "string" &&
  (LINK_PREVIEW_TYPES as readonly string[]).includes(value);
